import OrderedTicket, {
    OrderedTicketItem,
} from 'components/shared/OrderedTicket'
import { useEffect, useState } from 'react'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import { getDayNameAndDate } from 'helpers/dates'
import CabanaEditor from 'routes/selfcare/views/ManageOrder/editors/CabanaEditor'
import { useAutoScroll } from 'utils/hooks'
import {
    CabanaProductEditResponseDto,
    CabanaReservation,
} from 'store/selfcare-slice/types'

interface CabanaTicketProps {
    cabana: CabanaReservation
    index: number
}

export interface InitialData {
    unitPrice?: number
    productId?: string
}

export type ChangesDataAttr = {
    date?: string
}

const CabanaTicket = ({ cabana, index }: CabanaTicketProps) => {
    const { calculateScroll } = useAutoScroll()

    const { original, changeType, changes, isActive, modificationId } = cabana

    const [isEdited, setIsEdited] = useState(false)
    const [isActionErrors, setIsActionErrors] = useState(false)
    const [temporaryChanges, setTemporaryChanges] = useState(
        changes || original
    )

    const { detail } = temporaryChanges || {}

    const { date } = detail || {}

    const { deleteItem, revertDeleteItem } = useSelfcare()

    const {
        detail: { date: originalDate, orderCabanaId },
        price: originalPrice,
    } = changes || original

    const cabanaPrice = originalPrice || 0

    const handleDelete = () => {
        setIsActionErrors(false)
        deleteItem({ productType: 'cabana', detail: { orderCabanaId } }).then(
            ({ isSuccess }) => {
                if (isSuccess) {
                    setIsEdited(false)
                    setTemporaryChanges(changes || original)
                } else {
                    setIsActionErrors(true)
                }
            }
        )
    }

    const handleRevert = () => {
        if (!modificationId) return

        revertDeleteItem(modificationId)
    }

    const handleCancel = () => {
        setIsEdited(false)
        setTemporaryChanges(changes || original)
        calculateScroll({
            trigerringSection: 'cabanas',
            trigerringAction: 'cancel_edit',
            targetId: orderCabanaId,
        })
    }

    const handleChanges = (data: ChangesDataAttr) => {
        const { date: changedDate } = data

        const changes: CabanaProductEditResponseDto = {
            ...temporaryChanges,
            detail: {
                ...detail,
                date: changedDate || date,
            },
        }

        setTemporaryChanges(changes)
    }

    const cabanaItem: OrderedTicketItem[] = [
        {
            text: 'Cabana',
            date: date && getDayNameAndDate({ date: originalDate }),
        },
    ]

    const editedProperty =
        changes?.detail.date && changes?.detail.date !== original.detail.date
            ? 'date'
            : undefined

    useEffect(() => {
        setTemporaryChanges(changes || original)
    }, [modificationId])

    return (
        <OrderedTicket
            id={`cabana-${orderCabanaId}`}
            key={orderCabanaId}
            items={cabanaItem}
            price={cabanaPrice}
            isEdited={isEdited}
            setIsEdited={setIsEdited}
            withEditEnabled={!!originalDate}
            isDisabled={!isActive}
            onDelete={handleDelete}
            editedItem={editedProperty}
            creator={
                <CabanaEditor
                    handleCancel={handleCancel}
                    currentPrice={cabanaPrice}
                    cabana={temporaryChanges}
                    setChanges={handleChanges}
                    isModified={
                        JSON.stringify(temporaryChanges) !==
                        JSON.stringify(changes || original)
                    }
                    index={index}
                />
            }
            isDeleted={isActive && changeType === 'return'}
            onRevert={handleRevert}
            onEdit={() => {
                calculateScroll({
                    trigerringSection: 'cabanas',
                    trigerringAction: 'edit',
                    targetId: orderCabanaId,
                })
            }}
            isActionError={isActionErrors}
            testIdPrefix={`selfcare-cabana-${index}`}
        />
    )
}
export default CabanaTicket
